import { getAddress, type Address, type Hash } from "viem";
import { CHAIN_ID, DATA_API_URL, WALLET_ADDRESS } from "./config";
import type { Erc20Transfer } from "./types";

interface DataApiTransaction {
  blockNumber?: unknown;
  blockTimestamp?: unknown;
  txHash?: unknown;
  logIndex?: unknown;
  value?: unknown;
  from?: { address?: unknown };
  to?: { address?: unknown };
  erc20Token?: {
    address?: unknown;
    decimals?: unknown;
  };
}

interface DataApiPage {
  transactions?: unknown;
  nextPageToken?: unknown;
}

function parseAddress(value: unknown, field: string): Address {
  if (typeof value !== "string") throw new Error(`流水缺少 ${field}`);
  try {
    return getAddress(value.toLowerCase());
  } catch {
    throw new Error(`流水中的 ${field} 地址无效`);
  }
}

function parseInteger(value: unknown, field: string): number {
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed) || parsed < 0) {
    throw new Error(`流水中的 ${field} 无效`);
  }
  return parsed;
}

function parseBigInt(value: unknown, field: string): bigint {
  if (
    (typeof value !== "string" && typeof value !== "number") ||
    !/^\d+$/.test(String(value))
  ) {
    throw new Error(`流水中的 ${field} 无效`);
  }
  return BigInt(String(value));
}

export function parseDataApiTransaction(value: unknown): Erc20Transfer {
  if (!value || typeof value !== "object") throw new Error("流水格式无效");
  const item = value as DataApiTransaction;
  if (typeof item.txHash !== "string" || !/^0x[0-9a-fA-F]{64}$/.test(item.txHash)) {
    throw new Error("流水交易哈希无效");
  }
  if (typeof item.value !== "string" || !/^\d+$/.test(item.value)) {
    throw new Error("流水代币数量无效");
  }

  return {
    blockNumber: parseBigInt(item.blockNumber, "blockNumber"),
    blockTimestamp: parseInteger(item.blockTimestamp, "blockTimestamp"),
    txHash: item.txHash as Hash,
    logIndex: parseInteger(item.logIndex, "logIndex"),
    from: parseAddress(item.from?.address, "from"),
    to: parseAddress(item.to?.address, "to"),
    value: parseBigInt(item.value, "value"),
    tokenAddress: parseAddress(item.erc20Token?.address, "token"),
    tokenDecimals: parseInteger(item.erc20Token?.decimals, "tokenDecimals"),
  };
}

export async function fetchAllErc20Transfers(
  signal?: AbortSignal,
  fetchImpl: typeof fetch = fetch,
): Promise<Erc20Transfer[]> {
  const transfers: Erc20Transfer[] = [];
  let pageToken: string | undefined;

  for (let page = 0; page < 100; page += 1) {
    const url = new URL(
      `${DATA_API_URL}/chains/${CHAIN_ID}/addresses/${WALLET_ADDRESS}/transactions:listErc20`,
    );
    url.searchParams.set("pageSize", "100");
    if (pageToken) url.searchParams.set("pageToken", pageToken);

    const response = await fetchImpl(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    });
    if (!response.ok) {
      throw new Error(`Avalanche Data API 请求失败（HTTP ${response.status}）`);
    }

    const payload = (await response.json()) as DataApiPage;
    if (!Array.isArray(payload.transactions)) {
      throw new Error("Avalanche Data API 返回格式无效");
    }
    transfers.push(...payload.transactions.map(parseDataApiTransaction));

    if (payload.nextPageToken === undefined || payload.nextPageToken === null) {
      return transfers;
    }
    if (
      typeof payload.nextPageToken !== "string" ||
      payload.nextPageToken.length === 0
    ) {
      throw new Error("Avalanche Data API 分页标记无效");
    }
    pageToken = payload.nextPageToken;
  }

  throw new Error("Avalanche Data API 分页过多，已停止读取");
}
